import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalSelect } from "@/components/ui/BrutalSelect";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { Copy, Send } from "lucide-react";

interface EmailTemplateModalProps {
  template: any;
  open: boolean;
  onClose: () => void;
}

export function EmailTemplateModal({ template, open, onClose }: EmailTemplateModalProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    template_name: template.template_name,
    category: template.category,
    is_active: template.is_active,
    subject_it: template.subject_it,
    subject_en: template.subject_en,
    subject_es: template.subject_es,
    body_html_it: template.body_html_it,
    body_html_en: template.body_html_en,
    body_html_es: template.body_html_es,
    body_text_it: template.body_text_it || "",
    body_text_en: template.body_text_en || "",
    body_text_es: template.body_text_es || "",
  });

  const variables = template.variables || [];

  const handleSave = async () => {
    try {
      setSaving(true);
      const { error } = await supabase
        .from("email_templates")
        .update(formData)
        .eq("id", template.id);

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success") || "Successo",
        description: t("admin.settings.email.templates.messages.saveSuccess") || "Template salvato con successo",
      });
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: t("admin.settings.toast.error") || "Errore",
        description: t("admin.settings.email.templates.messages.saveError") || "Errore nel salvataggio del template",
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    toast({
      description: t("admin.settings.email.templates.messages.variableCopied") || "Variabile copiata negli appunti",
    });
  };

  const handleSendTest = async () => {
    try {
      const { error } = await supabase.functions.invoke("test-email-template", {
        body: {
          templateKey: template.template_key,
          language: "it",
        },
      });

      if (error) throw error;

      toast({
        title: t("admin.settings.toast.success") || "Successo",
        description: t("admin.settings.email.templates.messages.testEmailSent") || "Email di test inviata con successo",
      });
    } catch (error) {
      console.error("Error sending test email:", error);
      toast({
        title: t("admin.settings.toast.error") || "Errore",
        description: t("admin.settings.email.templates.messages.testEmailError") || "Errore nell'invio dell'email di test",
        variant: "error",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto border-[3px] border-dark shadow-[8px_8px_0px_0px] shadow-dark">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {t("admin.settings.email.templates.modal.title") || "Modifica Template Email"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* General Info */}
          <div className="border-2 border-dark p-4 bg-surface">
            <h3 className="font-bold mb-4">{t("admin.settings.email.templates.modal.generalInfo") || "Informazioni Generali"}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("admin.settings.email.templates.modal.templateKey") || "Chiave Template"}</Label>
                <BrutalInput value={template.template_key} disabled />
              </div>
              <div>
                <Label>{t("admin.settings.email.templates.modal.templateName") || "Nome Template"}</Label>
                <BrutalInput
                  value={formData.template_name}
                  onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                />
              </div>
              <div>
                <Label>{t("admin.settings.email.templates.modal.category") || "Categoria"}</Label>
                <BrutalSelect
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <option value="auth">{t("admin.settings.email.templates.categories.auth") || "Autenticazione"}</option>
                  <option value="subscription">{t("admin.settings.email.templates.categories.subscription") || "Abbonamento"}</option>
                  <option value="payout">{t("admin.settings.email.templates.categories.payout") || "Pagamenti"}</option>
                  <option value="event">{t("admin.settings.email.templates.categories.event") || "Eventi"}</option>
                  <option value="review">{t("admin.settings.email.templates.categories.review") || "Recensioni"}</option>
                  <option value="jobs">{t("admin.settings.email.templates.categories.jobs") || "Annunci Lavoro"}</option>
                </BrutalSelect>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label>{t("admin.settings.email.templates.modal.isActive") || "Attivo"}</Label>
              </div>
            </div>
          </div>

          {/* Available Variables */}
          {variables.length > 0 && (
            <div className="border-2 border-dark p-4 bg-primary/5">
              <h3 className="font-bold mb-2">{t("admin.settings.email.templates.modal.availableVariables") || "Variabili Disponibili"}</h3>
              <p className="text-sm text-muted-foreground mb-3">
                {t("admin.settings.email.templates.modal.variablesHelp") || "Clicca su una variabile per copiarla o usa il bottone nella toolbar per inserirla nell'editor"}
              </p>
              <div className="flex flex-wrap gap-2">
                {variables.map((variable: string) => (
                  <button
                    key={variable}
                    onClick={() => handleCopyVariable(variable)}
                    className="px-3 py-1 bg-white border-2 border-dark font-mono text-sm hover:bg-primary/10 transition-colors flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3" />
                    {`{{${variable}}}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content Tabs */}
          <div className="border-2 border-dark p-4">
            <h3 className="font-bold mb-4">{t("admin.settings.email.templates.modal.content") || "Contenuto"}</h3>
            <Tabs defaultValue="it">
              <TabsList>
                <TabsTrigger value="it">{t("admin.settings.email.templates.modal.languages.it") || "Italiano"}</TabsTrigger>
                <TabsTrigger value="en">{t("admin.settings.email.templates.modal.languages.en") || "Inglese"}</TabsTrigger>
                <TabsTrigger value="es">{t("admin.settings.email.templates.modal.languages.es") || "Spagnolo"}</TabsTrigger>
              </TabsList>

              {["it", "en", "es"].map((lang) => (
                <TabsContent key={lang} value={lang} className="space-y-4">
                  <div>
                    <Label>{t("admin.settings.email.templates.modal.subject") || "Oggetto"}</Label>
                    <BrutalInput
                      value={formData[`subject_${lang}` as keyof typeof formData]}
                      onChange={(e) => setFormData({ ...formData, [`subject_${lang}`]: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.settings.email.templates.modal.bodyHtml") || "Corpo HTML"}</Label>
                    <RichTextEditor
                      value={formData[`body_html_${lang}` as keyof typeof formData] as string}
                      onChange={(html) => setFormData({ ...formData, [`body_html_${lang}`]: html })}
                      placeholder={t("admin.settings.email.templates.modal.bodyHtmlPlaceholder") || "Scrivi il contenuto HTML dell'email..."}
                      customVariables={variables}
                    />
                  </div>
                  <div>
                    <Label>{t("admin.settings.email.templates.modal.bodyText") || "Corpo Testo"}</Label>
                    <BrutalTextarea
                      value={formData[`body_text_${lang}` as keyof typeof formData]}
                      onChange={(e) => setFormData({ ...formData, [`body_text_${lang}`]: e.target.value })}
                      rows={6}
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t-2 border-dark">
            <BrutalButton variant="secondary" onClick={onClose}>
              {t("admin.settings.email.templates.modal.buttons.cancel") || "Annulla"}
            </BrutalButton>
            <BrutalButton variant="secondary" onClick={handleSendTest}>
              <Send className="h-4 w-4 mr-2" />
              {t("admin.settings.email.templates.modal.buttons.sendTest") || "Invia Test"}
            </BrutalButton>
            <BrutalButton onClick={handleSave} disabled={saving}>
              {saving ? "..." : (t("admin.settings.email.templates.modal.buttons.save") || "Salva")}
            </BrutalButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
